<template>
    <div>
        <div
            class="field"
            @mousedown="onInternalPointerDown"
            @mouseup="onInternalPointerUp"
            @touchstart="onInternalPointerDown"
            @touchend="onInternalPointerUp"
        >
            <e-taginput
                ref="taginput"
                v-model="selectedTags"
                :data="filteredTags"
                open-on-focus
                autocomplete
                allow-new
                field="name.en"
                class="control"
                @typing="getFilteredTags"
                @add="saveSelectedTags"
                @remove="saveSelectedTags"
                @blur="commitOnBlur($event)"
            >
                <template #default="{ option }">
                    {{ option.name.en }}
                </template>
            </e-taginput>
        </div>
    </div>
</template>

<script>
import find from 'lodash/find.js';
import { nextTick } from 'vue';

export default {
    name: 'TagSwitcher',

    props: {
        isManualSave: {
            type: Boolean,
            required: false,
        },
        tags: {
            type: Array,
            required: true,
        },

        usesTagType: {
            type: Boolean,
            required: false,
            default: false,
        },

        tagType: {
            type: String,
            required: true,
        },

        subject: {
            type: Object,
            required: true,
        },

        action: {
            type: String,
            required: false,
            default: '',
        },

        modelKey: {
            type: String,
            required: false,
            default: 'id',
        },
    },

    emits: ['syncTags'],

    data: () => ({
        filteredTags: [],
        currentInputText: '',
        selectedTags: [],
        isSaving: false,
        isInteractingWithin: false,
    }),

    computed: {
        currentSubjectTags() {
            return this.subject?.tags ?? [];
        },
    },

    watch: {
        subject(newSubject, oldSubject) {
            // Only reload when the subject actually changes (e.g. switching to a different note).
            // The parent rebuilds the subject object on every tag edit (same id), and reloading on
            // those churns reset selectedTags — dropping tags added consecutively (ClickUp 868fzvgdx).
            if (newSubject?.id !== oldSubject?.id) {
                this.loadTags();
            }
        },
    },

    created() {
        this.loadTags();
    },

    methods: {
        getFilteredTags(text) {
            this.currentInputText = text;

            this.filteredTags = this.tags.filter(tag => {
                return (
                    tag.name.en?.toLowerCase().indexOf(text.toLowerCase()) >= 0 &&
                    !find(this.selectedTags, { id: tag.id })
                );
            });
        },

        async saveSelectedTags() {
            await nextTick();

            const tags = Array.isArray(this.selectedTags) ? [...this.selectedTags] : [];

            if (!this.isManualSave && this.subject.id) {
                this.isSaving = true;

                await this.$store.dispatch('AutoSave/setSaving', this.action);

                const result = await this.$store.dispatch(this.action, {
                    subject: this.subject,
                    tags,
                    modelKey: this.modelKey,
                    tagType: this.usesTagType ? this.tagType : null,
                });

                this.isSaving = false;

                if (result instanceof Error) {
                    this.$edverumToast.open({
                        message: 'Unable to save tags. Please check your connection and try again.',
                        type: 'is-danger',
                        position: 'is-bottom-left',
                    });

                    await this.$store.dispatch('AutoSave/setError', { indicator: this.action, error: result });

                    this.loadTags();

                    return;
                }

                await this.$store.dispatch('AutoSave/setSaved', this.action);

                // Keep parent state synchronized to prevent stale tag payloads on full form save.
                this.$emit('syncTags', tags);

                this.reset();

                this.getFilteredTags('');
            } else {
                this.$emit('syncTags', tags);
            }
        },

        reset() {
            this.filteredTags = [];

            this.currentInputText = '';
        },

        loadTags() {
            const { tags } = this.subject;

            if (!tags || typeof tags !== 'object' || typeof tags === 'function' || Array.isArray(tags) === false) {
                this.selectedTags = [];
            } else if (this.usesTagType) {
                this.selectedTags = tags.filter(tag => tag.type === this.tagType);
            } else {
                this.selectedTags = tags.filter(tag => !tag.type);
            }

            this.getFilteredTags('');
        },

        async commitOnBlur(event) {
            // If the next focused element is still within the tag input component,
            // or if a pointer interaction started inside (e.g., selecting a suggestion),
            // do nothing.
            const tagInputRoot = this.$refs.taginput && this.$refs.taginput.$el ? this.$refs.taginput.$el : null;
            const nextFocused = (event && (event.relatedTarget || document.activeElement)) || null;

            if (tagInputRoot) {
                if (this.isInteractingWithin) {
                    return;
                }

                if (nextFocused && tagInputRoot.contains(nextFocused)) {
                    return;
                }
            }

            // Do NOT commit the current input as a tag on blur. Only clear the input
            // and suggestion list so that tags are added only via explicit actions
            // (e.g. pressing Enter which triggers the `@add` handler).
            this.clearTagInputElement(event);
            this.currentInputText = '';
            this.getFilteredTags('');
        },

        onInternalPointerDown(event) {
            const tagInputRoot = this.$refs.taginput && this.$refs.taginput.$el ? this.$refs.taginput.$el : null;

            this.isInteractingWithin = !!(tagInputRoot && event && event.target && tagInputRoot.contains(event.target));
        },

        onInternalPointerUp() {
            this.isInteractingWithin = false;
        },

        normalizeInput(text) {
            return (text || '').toString().trim()?.toLowerCase();
        },

        tagMatchesCurrentType(tag) {
            return this.usesTagType ? tag.type === this.tagType : tag.type === null;
        },

        isAlreadySelectedByIdOrName(normalizedName, candidate) {
            if (candidate && candidate.id && find(this.selectedTags, { id: candidate.id })) {
                return true;
            }

            return this.selectedTags.some(existing => {
                const existingName =
                    typeof existing === 'string'
                        ? this.normalizeInput(existing)
                        : this.normalizeInput(existing?.name?.en);

                const typeMatches = typeof existing === 'string' ? true : this.tagMatchesCurrentType(existing);

                return existingName === normalizedName && typeMatches;
            });
        },

        prepareTagToAdd(rawInput, normalizedName) {
            const existing =
                this.tags.find(
                    tag => this.normalizeInput(tag?.name?.en) === normalizedName && this.tagMatchesCurrentType(tag)
                ) || null;

            if (this.isAlreadySelectedByIdOrName(normalizedName, existing)) return null;

            return (
                existing || { id: null, name: { en: rawInput.trim() }, type: this.usesTagType ? this.tagType : null }
            );
        },

        clearTagInputElement(event) {
            const inputEl = this.getNativeInputEl(event);

            if (inputEl) {
                inputEl.value = '';

                try {
                    inputEl.dispatchEvent(new Event('input', { bubbles: true }));
                } catch (e) {
                    //
                }
            }
        },

        getNativeInputEl(event) {
            if (event?.target?.tagName === 'INPUT') {
                return event.target;
            }

            if (this.$refs.taginput?.$el) {
                return this.$refs.taginput.$el.querySelector('input');
            }

            return null;
        },
    },
};
</script>
